import React, { useState, useEffect } from "react";
import { useAuth, useUser } from "@clerk/clerk-react";
import { UpgradePromptDialog } from "@/components/billing/UpgradePromptDialog";
import { DashboardLayout } from "@/components/dashboard/dashboard-layout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Plus,
  MoreHorizontal,
  Search,
  Plug,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Trash2,
  RefreshCw,
  Loader2,
  Mail,
  Sparkles,
  Globe,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  SiQuickbooks,
  SiNotion,
  SiGmail,
  SiGooglecalendar,
  SiGooglesheets,
  SiGoogleslides,
  SiGoogledocs,
  SiGoogledrive,
  SiGooglemaps,
  SiHubspot,
  SiSalesforce,
  SiAsana,
  SiClickup,
  SiLinear,
  SiJira,
  SiAirtable,
  SiZendesk,
  SiIntercom,
  SiCalendly,
  SiTypeform,
  SiXero,
  SiStripe,
  SiGusto,
  SiDropbox,
} from "react-icons/si";
import {
  FaLinkedin,
  FaMicrosoft,
} from "react-icons/fa";
import { Layers, Sparkles as SparklesIcon } from "lucide-react";
import { useQuery, useAction } from "convex/react";
import { api } from "../../../../../../convex/_generated/api";
import { toast } from "sonner";
import { EmptyState } from "@/components/ui/empty-state";
import { useAppAuth } from "@/contexts/AuthContext";

// Connection type definitions matching Composio / Convex response
interface Connection {
  id: string;
  platformVersion: string;
  name: string | null;
  type: string;
  key: string;
  environment: string;
  platform: string;
  identity: string;
  identityType: "user" | "team" | "organization" | "project";
  description: string;
  accountIdentifier?: string | null;
  tags: string[];
  version: string;
  active: boolean;
  createdAt: string;
}

export type IntegrationCategory =
  | "all"
  | "communication"
  | "sales"
  | "operations"
  | "finance"
  | "docs";

interface IntegrationOption {
  slug: string;
  name: string;
  category: "communication" | "sales" | "operations" | "finance" | "docs";
  description: string;
  usedBy: string[];
  icon: React.ReactNode;
  borderHoverColor: string;
  badgeBgColor: string;
  badgeTextColor: string;
}

const AVAILABLE_INTEGRATIONS: IntegrationOption[] = [
  // --- Communication & Messaging ---
  {
    slug: "agent_mail",
    name: "Agent Mail",
    category: "communication",
    description: "Composio autonomous email inboxes for agents to receive, parse, and send emails.",
    usedBy: ["Operations", "Account Mgr", "Billing", "HR"],
    icon: <Mail className="h-6 w-6 text-emerald-600" />,
    borderHoverColor: "hover:border-emerald-500",
    badgeBgColor: "bg-emerald-50 border-emerald-100",
    badgeTextColor: "text-emerald-700",
  },
  {
    slug: "gmail",
    name: "Gmail",
    category: "communication",
    description: "Send formal client proposals and past-due notices directly from your Google account.",
    usedBy: ["Account Mgr", "Billing"],
    icon: <SiGmail className="h-6 w-6 text-red-600" />,
    borderHoverColor: "hover:border-red-500",
    badgeBgColor: "bg-red-50 border-red-100",
    badgeTextColor: "text-red-600",
  },
  {
    slug: "outlook",
    name: "Microsoft Outlook",
    category: "communication",
    description: "Draft and dispatch official emails from Microsoft 365 executive mailboxes.",
    usedBy: ["Account Mgr", "Billing", "Chief of Staff"],
    icon: <FaMicrosoft className="h-6 w-6 text-[#0078D4]" />,
    borderHoverColor: "hover:border-[#0078D4]",
    badgeBgColor: "bg-blue-50 border-blue-100",
    badgeTextColor: "text-[#0078D4]",
  },

  // --- Sales, Leads & CRM ---
  {
    slug: "apollo",
    name: "Apollo.io",
    category: "sales",
    description: "Retrieve verified property manager emails, direct phone numbers, and title details.",
    usedBy: ["Lead Scout"],
    icon: (
      <img
        src="https://t3.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=http://apollo.io&size=128"
        alt="Apollo.io"
        className="h-6 w-6 object-contain"
      />
    ),
    borderHoverColor: "hover:border-purple-500",
    badgeBgColor: "bg-purple-50 border-purple-100",
    badgeTextColor: "text-purple-600",
  },
  {
    slug: "hubspot",
    name: "HubSpot",
    category: "sales",
    description: "Sync qualified prospect lead batches directly into HubSpot CRM pipelines.",
    usedBy: ["Lead Scout", "Account Mgr"],
    icon: <SiHubspot className="h-6 w-6 text-[#FF7A59]" />,
    borderHoverColor: "hover:border-[#FF7A59]",
    badgeBgColor: "bg-orange-50 border-orange-100",
    badgeTextColor: "text-[#FF7A59]",
  },
  {
    slug: "gohighlevel",
    name: "GoHighLevel",
    category: "sales",
    description: "Sync commercial lead pipelines, opportunities, automated outreach, and CRM contacts.",
    usedBy: ["Lead Scout", "Account Mgr"],
    icon: (
      <img
        src="https://t3.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=http://gohighlevel.com&size=128"
        alt="GoHighLevel"
        className="h-6 w-6 object-contain"
      />
    ),
    borderHoverColor: "hover:border-blue-600",
    badgeBgColor: "bg-blue-50 border-blue-100",
    badgeTextColor: "text-blue-600",
  },
  {
    slug: "salesforce",
    name: "Salesforce",
    category: "sales",
    description: "Export commercial leads and track facility accounts across enterprise pipelines.",
    usedBy: ["Lead Scout", "Account Mgr"],
    icon: <SiSalesforce className="h-6 w-6 text-[#00A1E0]" />,
    borderHoverColor: "hover:border-[#00A1E0]",
    badgeBgColor: "bg-sky-50 border-sky-100",
    badgeTextColor: "text-[#00A1E0]",
  },
  {
    slug: "pipedrive",
    name: "Pipedrive",
    category: "sales",
    description: "Streamline sales deal stages, activity reminders, and client follow-ups.",
    usedBy: ["Lead Scout", "Account Mgr"],
    icon: <Layers className="h-6 w-6 text-emerald-600" />,
    borderHoverColor: "hover:border-emerald-600",
    badgeBgColor: "bg-emerald-50 border-emerald-100",
    badgeTextColor: "text-emerald-700",
  },
  {
    slug: "linkedin",
    name: "LinkedIn",
    category: "sales",
    description: "Verify property manager tenure and map management firm organizational hierarchies.",
    usedBy: ["Lead Scout"],
    icon: <FaLinkedin className="h-6 w-6 text-[#0A66C2]" />,
    borderHoverColor: "hover:border-[#0A66C2]",
    badgeBgColor: "bg-blue-50 border-blue-100",
    badgeTextColor: "text-[#0A66C2]",
  },
  {
    slug: "tavily",
    name: "Tavily Search",
    category: "sales",
    description: "Autonomous web crawling for local commercial real estate directories and properties.",
    usedBy: ["Lead Scout", "Chief of Staff"],
    icon: <Globe className="h-6 w-6 text-blue-600" />,
    borderHoverColor: "hover:border-blue-500",
    badgeBgColor: "bg-blue-50 border-blue-100",
    badgeTextColor: "text-blue-600",
  },
  {
    slug: "firecrawl",
    name: "Firecrawl",
    category: "sales",
    description: "Deep web scraping of commercial real estate listings, tenant directories, and property manager portals.",
    usedBy: ["Lead Scout"],
    icon: (
      <img
        src="https://t3.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=http://firecrawl.dev&size=128"
        alt="Firecrawl"
        className="h-6 w-6 object-contain"
      />
    ),
    borderHoverColor: "hover:border-orange-500",
    badgeBgColor: "bg-orange-50 border-orange-100",
    badgeTextColor: "text-orange-600",
  },
  {
    slug: "exa",
    name: "Exa Neural Search",
    category: "sales",
    description: "AI-native semantic search engine for pinpointing commercial real estate and business facilities.",
    usedBy: ["Lead Scout"],
    icon: (
      <img
        src="https://t3.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=http://exa.ai&size=128"
        alt="Exa"
        className="h-6 w-6 object-contain"
      />
    ),
    borderHoverColor: "hover:border-indigo-500",
    badgeBgColor: "bg-indigo-50 border-indigo-100",
    badgeTextColor: "text-indigo-600",
  },
  {
    slug: "hunter",
    name: "Hunter.io",
    category: "sales",
    description: "Domain search and email verification for property management companies and asset managers.",
    usedBy: ["Lead Scout"],
    icon: (
      <img
        src="https://t3.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=http://hunter.io&size=128"
        alt="Hunter.io"
        className="h-6 w-6 object-contain"
      />
    ),
    borderHoverColor: "hover:border-amber-500",
    badgeBgColor: "bg-amber-50 border-amber-100",
    badgeTextColor: "text-amber-700",
  },
  {
    slug: "clearbit",
    name: "Clearbit",
    category: "sales",
    description: "Enrich company profiles, facility square footage, and corporate intelligence.",
    usedBy: ["Lead Scout"],
    icon: <SparklesIcon className="h-6 w-6 text-blue-600" />,
    borderHoverColor: "hover:border-blue-500",
    badgeBgColor: "bg-blue-50 border-blue-100",
    badgeTextColor: "text-blue-600",
  },

  // --- Operations & Task Management ---
  {
    slug: "googlecalendar",
    name: "Google Calendar",
    category: "operations",
    description: "Seamless scheduling for client walkthroughs, leadership reviews, and operational crons.",
    usedBy: ["Chief of Staff", "Operations"],
    icon: <SiGooglecalendar className="h-6 w-6 text-blue-600" />,
    borderHoverColor: "hover:border-blue-500",
    badgeBgColor: "bg-blue-50 border-blue-100",
    badgeTextColor: "text-blue-600",
  },
  {
    slug: "googlemaps",
    name: "Google Maps",
    category: "operations",
    description: "Calculate cleaner transit distances, route efficiency, and site geofences.",
    usedBy: ["Operations"],
    icon: <SiGooglemaps className="h-6 w-6 text-[#EA4335]" />,
    borderHoverColor: "hover:border-red-500",
    badgeBgColor: "bg-red-50 border-red-100",
    badgeTextColor: "text-red-600",
  },
  {
    slug: "asana",
    name: "Asana",
    category: "operations",
    description: "Create and track facility maintenance work orders and deep clean task lists.",
    usedBy: ["Operations", "Chief of Staff"],
    icon: <SiAsana className="h-6 w-6 text-[#F06A6A]" />,
    borderHoverColor: "hover:border-[#F06A6A]",
    badgeBgColor: "bg-pink-50 border-pink-100",
    badgeTextColor: "text-[#F06A6A]",
  },
  {
    slug: "monday",
    name: "Monday.com",
    category: "operations",
    description: "Sync field dispatch boards, cleaner equipment checkouts, and site schedules.",
    usedBy: ["Operations"],
    icon: <Layers className="h-6 w-6 text-[#FF3D57]" />,
    borderHoverColor: "hover:border-[#FF3D57]",
    badgeBgColor: "bg-rose-50 border-rose-100",
    badgeTextColor: "text-[#FF3D57]",
  },
  {
    slug: "clickup",
    name: "ClickUp",
    category: "operations",
    description: "Manage cross-departmental executive tasks and facility work order tickets.",
    usedBy: ["Operations", "Chief of Staff"],
    icon: <SiClickup className="h-6 w-6 text-[#7B68EE]" />,
    borderHoverColor: "hover:border-[#7B68EE]",
    badgeBgColor: "bg-purple-50 border-purple-100",
    badgeTextColor: "text-[#7B68EE]",
  },
  {
    slug: "linear",
    name: "Linear",
    category: "operations",
    description: "Streamline back-office bug tracking, software requests, and internal improvements.",
    usedBy: ["Chief of Staff"],
    icon: <SiLinear className="h-6 w-6 text-[#5E6AD2]" />,
    borderHoverColor: "hover:border-[#5E6AD2]",
    badgeBgColor: "bg-indigo-50 border-indigo-100",
    badgeTextColor: "text-[#5E6AD2]",
  },
  {
    slug: "jira",
    name: "Jira",
    category: "operations",
    description: "Log inspection defects, assign re-clean tickets, and track SLA compliance.",
    usedBy: ["Quality Control"],
    icon: <SiJira className="h-6 w-6 text-[#0052CC]" />,
    borderHoverColor: "hover:border-[#0052CC]",
    badgeBgColor: "bg-blue-50 border-blue-100",
    badgeTextColor: "text-[#0052CC]",
  },
  {
    slug: "airtable",
    name: "Airtable",
    category: "operations",
    description: "Manage real-time cleaning supply inventory, site equipment logs, and inspection databases.",
    usedBy: ["Operations", "Quality Control"],
    icon: <SiAirtable className="h-6 w-6 text-[#18BFFF]" />,
    borderHoverColor: "hover:border-[#18BFFF]",
    badgeBgColor: "bg-cyan-50 border-cyan-100",
    badgeTextColor: "text-cyan-700",
  },
  {
    slug: "zendesk",
    name: "Zendesk",
    category: "operations",
    description: "Monitor customer complaints, track ticket resolution times, and log sentiment.",
    usedBy: ["Account Mgr"],
    icon: <SiZendesk className="h-6 w-6 text-[#03363D]" />,
    borderHoverColor: "hover:border-teal-700",
    badgeBgColor: "bg-teal-50 border-teal-100",
    badgeTextColor: "text-teal-900",
  },
  {
    slug: "intercom",
    name: "Intercom",
    category: "operations",
    description: "Real-time communication and support messages with commercial property managers.",
    usedBy: ["Account Mgr"],
    icon: <SiIntercom className="h-6 w-6 text-[#000000]" />,
    borderHoverColor: "hover:border-gray-800",
    badgeBgColor: "bg-gray-100 border-gray-200",
    badgeTextColor: "text-gray-900",
  },
  {
    slug: "calendly",
    name: "Calendly",
    category: "operations",
    description: "Auto-schedule quarterly account reviews and walkthrough appointments.",
    usedBy: ["Account Mgr"],
    icon: <SiCalendly className="h-6 w-6 text-[#006BFF]" />,
    borderHoverColor: "hover:border-[#006BFF]",
    badgeBgColor: "bg-blue-50 border-blue-100",
    badgeTextColor: "text-[#006BFF]",
  },
  {
    slug: "typeform",
    name: "Typeform",
    category: "operations",
    description: "Automated satisfaction surveys, NPS feedback forms, and site intake questionnaires.",
    usedBy: ["Account Mgr"],
    icon: <SiTypeform className="h-6 w-6 text-[#262627]" />,
    borderHoverColor: "hover:border-gray-700",
    badgeBgColor: "bg-gray-100 border-gray-200",
    badgeTextColor: "text-gray-900",
  },

  // --- Finance, Payroll & HR ---
  {
    slug: "quickbooks",
    name: "QuickBooks",
    category: "finance",
    description: "Automate invoicing, track accounts receivables, and record incoming payments.",
    usedBy: ["Billing & Finance"],
    icon: <SiQuickbooks className="h-6 w-6 text-green-700" />,
    borderHoverColor: "hover:border-green-600",
    badgeBgColor: "bg-green-50 border-green-100",
    badgeTextColor: "text-green-700",
  },
  {
    slug: "stripe",
    name: "Stripe",
    category: "finance",
    description: "Process credit card payments, deposit settlements, and client billing subscriptions.",
    usedBy: ["Billing & Finance"],
    icon: <SiStripe className="h-6 w-6 text-[#635BFF]" />,
    borderHoverColor: "hover:border-[#635BFF]",
    badgeBgColor: "bg-indigo-50 border-indigo-100",
    badgeTextColor: "text-[#635BFF]",
  },
  {
    slug: "xero",
    name: "Xero",
    category: "finance",
    description: "Synchronize invoices, aging balance sheets, and bank transaction statements.",
    usedBy: ["Billing & Finance"],
    icon: <SiXero className="h-6 w-6 text-[#13B5EA]" />,
    borderHoverColor: "hover:border-[#13B5EA]",
    badgeBgColor: "bg-sky-50 border-sky-100",
    badgeTextColor: "text-[#13B5EA]",
  },
  {
    slug: "gusto",
    name: "Gusto Payroll",
    category: "finance",
    description: "Verify cleaner hours against GPS logs and sync new hire employee profiles.",
    usedBy: ["Billing & Finance", "HR"],
    icon: <SiGusto className="h-6 w-6 text-[#F84A4B]" />,
    borderHoverColor: "hover:border-[#F84A4B]",
    badgeBgColor: "bg-red-50 border-red-100",
    badgeTextColor: "text-[#F84A4B]",
  },

  // --- Storage, Docs & Knowledge ---
  {
    slug: "notion",
    name: "Notion",
    category: "docs",
    description: "Access facility cleaning guidelines, chemical SOPs, and operational company wikis.",
    usedBy: ["Chief of Staff"],
    icon: <SiNotion className="h-6 w-6 text-gray-900" />,
    borderHoverColor: "hover:border-gray-500",
    badgeBgColor: "bg-gray-100 border-gray-200",
    badgeTextColor: "text-gray-900",
  },
  {
    slug: "googledrive",
    name: "Google Drive",
    category: "docs",
    description: "Store site inspection photos, background checks, and compliance certificates.",
    usedBy: ["Quality Control", "HR", "Chief of Staff"],
    icon: <SiGoogledrive className="h-6 w-6 text-[#0F9D58]" />,
    borderHoverColor: "hover:border-green-500",
    badgeBgColor: "bg-green-50 border-green-100",
    badgeTextColor: "text-green-700",
  },
  {
    slug: "googlesheets",
    name: "Google Sheets",
    category: "docs",
    description: "Export daily cleaner rosters, supply inventory sheets, and timecard audits.",
    usedBy: ["Operations", "Billing"],
    icon: <SiGooglesheets className="h-6 w-6 text-green-600" />,
    borderHoverColor: "hover:border-green-500",
    badgeBgColor: "bg-green-50 border-green-100",
    badgeTextColor: "text-green-600",
  },
  {
    slug: "googledocs",
    name: "Google Docs",
    category: "docs",
    description: "Generate and edit formal client inspection audit reports and proposals.",
    usedBy: ["Quality Control"],
    icon: <SiGoogledocs className="h-6 w-6 text-[#4285F4]" />,
    borderHoverColor: "hover:border-blue-500",
    badgeBgColor: "bg-blue-50 border-blue-100",
    badgeTextColor: "text-[#4285F4]",
  },
  {
    slug: "googleslides",
    name: "Google Slides",
    category: "docs",
    description: "Automate executive presentation updates and quarterly business review decks.",
    usedBy: ["Chief of Staff"],
    icon: <SiGoogleslides className="h-6 w-6 text-orange-600" />,
    borderHoverColor: "hover:border-orange-500",
    badgeBgColor: "bg-orange-50 border-orange-100",
    badgeTextColor: "text-orange-600",
  },
  {
    slug: "dropbox",
    name: "Dropbox",
    category: "docs",
    description: "High-resolution cloud storage for facility inspection photo logs and safety docs.",
    usedBy: ["Quality Control", "HR"],
    icon: <SiDropbox className="h-6 w-6 text-[#0061FF]" />,
    borderHoverColor: "hover:border-[#0061FF]",
    badgeBgColor: "bg-blue-50 border-blue-100",
    badgeTextColor: "text-[#0061FF]",
  },
];

export default function ConnectionsPage() {
  const { hasPermission } = useAppAuth();
  const canManageConnections = hasPermission("settings:write");
  const [searchQuery, setSearchQuery] = useState("");
  const [modalSearchQuery, setModalSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState<IntegrationCategory>("all");
  const [isLoading, setIsLoading] = useState(false);
  const [connections, setConnections] = useState<Connection[]>([]);
  const [connectionToDisconnect, setConnectionToDisconnect] = useState<{
    id: string;
    platformName: string;
  } | null>(null);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const { getToken } = useAuth();
  const { user } = useUser();
  const subscription = useQuery(api.subscriptions.getUserSubscription, user ? {} : "skip");
  const isFreePlan = !subscription?.amount || subscription.amount === 0;

  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showAddConnectionModal, setShowAddConnectionModal] = useState(false);

  const handleAddConnectionClick = () => {
    if (isFreePlan) {
      setShowUpgradeModal(true);
    } else {
      setShowAddConnectionModal(true);
    }
  };

  // Convex actions and mutations
  const listConnections = useAction(api.connections.list);
  const deleteConnection = useAction(api.connections.remove);
  const reconnectConnection = useAction(api.connections.reconnect);

  // Map active connections by platform slug for quick 1:1 lookup
  const connectedPlatformsMap = React.useMemo(() => {
    const map = new Map<string, Connection>();
    connections.forEach((conn) => {
      if (conn.active) {
        map.set(conn.platform.toLowerCase(), conn);
      }
    });
    return map;
  }, [connections]);

  // Identify duplicate active connections for warning banner
  const duplicatePlatforms = React.useMemo(() => {
    const counts = new Map<string, number>();
    connections.forEach((conn) => {
      if (conn.active) {
        const slug = conn.platform.toLowerCase();
        counts.set(slug, (counts.get(slug) || 0) + 1);
      }
    });
    return Array.from(counts.entries())
      .filter(([_, count]) => count > 1)
      .map(([slug]) => slug);
  }, [connections]);

  // Function to refresh connections
  const handleManualRefresh = async () => {
    try {
      setIsLoading(true);
      const result = await listConnections({});
      setConnections(result || []);
      toast.success("Connections refreshed");
    } catch (error) {
      console.error("Failed to refresh connections:", error);
      toast.error("Failed to refresh connections");
    } finally {
      setIsLoading(false);
    }
  };

  // Load connections on mount and setup focus listener
  useEffect(() => {
    const loadConnections = async () => {
      try {
        setIsLoading(true);
        const result = await listConnections({});
        setConnections(result || []);
      } catch (error) {
        console.error("Failed to load connections:", error);
        toast.error("Failed to load connections");
        setConnections([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadConnections();

    // Setup window focus listener to auto-refresh when returning from auth tab
    const handleFocus = () => {
      loadConnections();
    };

    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [listConnections]);

  // Composio connection flow
  const handleConnectApp = async (appName: string) => {
    try {
      setIsLoading(true);
      const { redirectUrl } = await reconnectConnection({ connectionKey: appName });
      if (redirectUrl) {
        // Open the Composio Connect Link in a new tab
        window.open(redirectUrl, "_blank");
        toast.info(`Please complete the connection in the new tab to connect ${appName}`);
      }
    } catch (error) {
      console.error("Failed to connect app:", error);
      toast.error(`Failed to connect ${appName}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisconnect = async () => {
    if (!connectionToDisconnect) return;

    const { id: connectionId, platformName } = connectionToDisconnect;

    try {
      setIsDisconnecting(true);
      await deleteConnection({ connectionId });
      toast.success(`${platformName} disconnected successfully`);

      // Reload connections after disconnect
      const result = await listConnections({});
      setConnections(result || []);
      setConnectionToDisconnect(null);
    } catch (error) {
      console.error("Failed to disconnect:", error);
      toast.error(`Failed to disconnect ${platformName}`);
    } finally {
      setIsDisconnecting(false);
    }
  };

  const handleReconnect = async (connectionKey: string, platformName: string) => {
    try {
      setIsLoading(true);
      const { redirectUrl } = await reconnectConnection({ connectionKey });

      if (redirectUrl) {
        window.open(redirectUrl, "_blank");
      }

      toast.success(`Redirecting to connect ${platformName}...`);
    } catch (error) {
      console.error("Failed to reconnect:", error);
      toast.error(`Failed to connect ${platformName}`);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusBadge = (active: boolean) => {
    if (active) {
      return (
        <Badge className="gap-1 border-green-200 bg-green-100 text-green-800 hover:bg-green-100">
          <CheckCircle2 className="h-3 w-3" />
          Active
        </Badge>
      );
    } else {
      return (
        <Badge className="gap-1 border-gray-200 bg-gray-100 text-gray-800 hover:bg-gray-100">
          <XCircle className="h-3 w-3" />
          Inactive
        </Badge>
      );
    }
  };

  const getTypeBadge = (type: string) => {
    return (
      <Badge variant="outline" className="font-mono text-xs">
        {type.toUpperCase()}
      </Badge>
    );
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const getPlatformDisplayName = (platform: string) => {
    const found = AVAILABLE_INTEGRATIONS.find(
      (item) => item.slug.toLowerCase() === platform.toLowerCase()
    );
    if (found) return found.name;

    // Convert platform slug to display name
    return platform
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const getPlatformIcon = (platform: string) => {
    const found = AVAILABLE_INTEGRATIONS.find(
      (item) => item.slug.toLowerCase() === platform.toLowerCase()
    );
    if (found) return found.icon;

    return <Plug className="h-4 w-4 text-blue-600" />;
  };

  // Filter connections in the main table based on search query
  const filteredConnections = connections.filter(
    (conn) =>
      conn.platform.toLowerCase().includes(searchQuery.toLowerCase()) ||
      conn.key.toLowerCase().includes(searchQuery.toLowerCase()) ||
      conn.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (conn.accountIdentifier &&
        conn.accountIdentifier.toLowerCase().includes(searchQuery.toLowerCase())) ||
      conn.type.toLowerCase().includes(searchQuery.toLowerCase()) ||
      conn.environment.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Filter available integrations inside the modal
  const filteredModalIntegrations = AVAILABLE_INTEGRATIONS.filter((item) => {
    const matchesCategory =
      activeCategory === "all" || item.category === activeCategory;
    const matchesSearch =
      item.name.toLowerCase().includes(modalSearchQuery.toLowerCase()) ||
      item.description.toLowerCase().includes(modalSearchQuery.toLowerCase()) ||
      item.usedBy.some((agent) =>
        agent.toLowerCase().includes(modalSearchQuery.toLowerCase())
      );
    return matchesCategory && matchesSearch;
  });

  return (
    <DashboardLayout activeTab="settings">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Connections</h1>
            <p className="mt-1 text-gray-500">
              Manage third-party integrations and connected services for automated workflows and AI Workforce agents.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={handleManualRefresh}
              disabled={isLoading}
              title="Refresh connections"
              className="bg-white"
            >
              <RefreshCw className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`} />
            </Button>
            {canManageConnections && (
              <Button className="gap-2" disabled={isLoading} onClick={handleAddConnectionClick}>
                <Plus className="h-4 w-4" />
                {isLoading ? "Loading..." : "Add Connection"}
              </Button>
            )}

            {/* Scrollable & Categorized Integration Modal */}
            <Dialog open={showAddConnectionModal} onOpenChange={setShowAddConnectionModal}>
              <DialogContent className="sm:max-w-[760px] max-h-[88vh] flex flex-col p-6 overflow-hidden">
                <DialogHeader className="shrink-0 pb-2">
                  <DialogTitle className="text-xl">Add & Manage Integrations</DialogTitle>
                  <DialogDescription>
                    Connect tools to empower your 7 AI Workforce agents. ManageClean uses a 1:1 connection model so each agent knows exactly which account to use.
                  </DialogDescription>
                </DialogHeader>

                {/* Search & Category Tabs (Fixed Header) */}
                <div className="shrink-0 space-y-3 pt-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    <Input
                      placeholder="Search integrations or agent names (e.g. Slack, HubSpot, Lead Scout)..."
                      className="pl-9 text-sm"
                      value={modalSearchQuery}
                      onChange={(e) => setModalSearchQuery(e.target.value)}
                    />
                  </div>

                  {/* Category Pills */}
                  <div className="flex flex-wrap items-center gap-1.5 border-b border-gray-100 pb-2 text-xs">
                    {[
                      { id: "all", label: "All Integrations" },
                      { id: "communication", label: "Communication" },
                      { id: "sales", label: "Sales & CRM" },
                      { id: "operations", label: "Operations" },
                      { id: "finance", label: "Finance & HR" },
                      { id: "docs", label: "Docs & Storage" },
                    ].map((cat) => (
                      <button
                        key={cat.id}
                        type="button"
                        onClick={() => setActiveCategory(cat.id as IntegrationCategory)}
                        className={`rounded-full px-3 py-1 font-medium transition-all ${
                          activeCategory === cat.id
                            ? "bg-blue-600 text-white shadow-xs"
                            : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                        }`}
                      >
                        {cat.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Scrollable Cards Grid Container */}
                <div className="flex-1 overflow-y-auto pr-1.5 pt-3 pb-2">
                  {filteredModalIntegrations.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                      <Plug className="h-8 w-8 text-gray-300 mb-2" />
                      <p className="text-sm font-medium text-gray-700">No integrations found</p>
                      <p className="text-xs text-gray-400 mt-1">Try searching for a different keyword or category.</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
                      {filteredModalIntegrations.map((integration) => {
                        const existingConn = connectedPlatformsMap.get(
                          integration.slug.toLowerCase()
                        );
                        const isConnected = !!existingConn;

                        return (
                          <Card
                            key={integration.slug}
                            className={`flex flex-col justify-between p-4 transition-all ${
                              isConnected
                                ? "border-green-200 bg-green-50/20 shadow-none"
                                : `cursor-pointer ${integration.borderHoverColor} hover:shadow-xs`
                            }`}
                            onClick={() => {
                              if (!isConnected) {
                                handleConnectApp(integration.slug);
                              }
                            }}
                          >
                            <div className="space-y-2">
                              <div className="flex items-center justify-between">
                                <div
                                  className={`rounded-lg border p-2 shadow-xs ${integration.badgeBgColor} ${integration.badgeTextColor}`}
                                >
                                  {integration.icon}
                                </div>
                                {isConnected ? (
                                  <Badge className="gap-1 border-green-200 bg-green-100 text-green-800 hover:bg-green-100">
                                    <CheckCircle2 className="h-3 w-3" />
                                    Connected
                                  </Badge>
                                ) : (
                                  <span className="text-xs font-medium text-gray-400">
                                    Available
                                  </span>
                                )}
                              </div>
                              <div>
                                <h3 className="text-sm font-semibold text-gray-900">
                                  {integration.name}
                                </h3>
                                <p className="mt-1 line-clamp-2 text-xs text-gray-500 leading-relaxed">
                                  {integration.description}
                                </p>
                              </div>

                              {/* Persona Badges */}
                              <div className="flex flex-wrap items-center gap-1 pt-1">
                                <span className="text-[10px] text-gray-400 font-medium">Used by:</span>
                                {integration.usedBy.map((agent) => (
                                  <span
                                    key={agent}
                                    className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-600"
                                  >
                                    {agent}
                                  </span>
                                ))}
                              </div>
                            </div>

                            {/* Card Footer / Action */}
                            {isConnected && existingConn ? (
                              <div className="mt-3 flex items-center justify-between border-t border-green-100 pt-2.5">
                                <span className="truncate text-xs font-medium text-gray-600 max-w-[150px]">
                                  {existingConn.accountIdentifier || "Active connection"}
                                </span>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 px-2 text-xs text-gray-600 hover:text-gray-900"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleConnectApp(integration.slug);
                                  }}
                                >
                                  <RefreshCw className="mr-1 h-3 w-3" />
                                  Switch Account
                                </Button>
                              </div>
                            ) : (
                              <div className="mt-3 border-t border-gray-100 pt-2.5">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="w-full text-xs h-8"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleConnectApp(integration.slug);
                                  }}
                                >
                                  Connect {integration.name}
                                </Button>
                              </div>
                            )}
                          </Card>
                        );
                      })}
                    </div>
                  )}
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Duplicate warning banner if existing legacy duplicates exist */}
        {duplicatePlatforms.length > 0 && (
          <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-900 shadow-xs">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
            <div className="text-sm">
              <span className="font-semibold">
                Multiple connections detected for: {duplicatePlatforms.map(getPlatformDisplayName).join(", ")}.
              </span>{" "}
              To ensure reliable AI Workforce execution with zero ambiguity, please disconnect redundant accounts below so each service has only one active primary connection.
            </div>
          </div>
        )}

        {/* Search and Filters */}
        <Card className="p-6">
          <div className="mb-6 flex gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
              <Input
                placeholder="Search connections by service, email, or account..."
                className="pl-8"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          {/* Connections Table */}
          <div className="w-full overflow-auto">
            <Table className="w-full">
              <TableHeader>
                <TableRow>
                  <TableHead>Service & Account</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Connected On</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredConnections.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="p-0">
                      <EmptyState
                        icon={Plug}
                        title={searchQuery ? "No connections found" : "No connections yet"}
                        description={
                          searchQuery
                            ? "No connections found matching your search."
                            : "Add your first integration to get started."
                        }
                      />
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredConnections.map((connection) => {
                    const platformName = getPlatformDisplayName(connection.platform);
                    return (
                      <TableRow key={connection.id}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="shrink-0 rounded-lg bg-blue-50 p-2">
                              {getPlatformIcon(connection.platform)}
                            </div>
                            <div className="flex flex-col">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-gray-900">{platformName}</span>
                                {connection.accountIdentifier && (
                                  <Badge
                                    variant="outline"
                                    className="border-gray-200 bg-gray-50 font-mono text-xs font-normal text-gray-700"
                                  >
                                    {connection.accountIdentifier}
                                  </Badge>
                                )}
                              </div>
                              <span className="text-xs text-gray-500">
                                {connection.description || `Connected ${platformName} account`}
                              </span>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>{getTypeBadge(connection.type)}</TableCell>
                        <TableCell>{getStatusBadge(connection.active)}</TableCell>
                        <TableCell>{formatDate(connection.createdAt)}</TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" disabled={isLoading}>
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() => handleReconnect(connection.key, platformName)}
                                disabled={isLoading}
                              >
                                <RefreshCw className="mr-2 h-4 w-4" />
                                {connection.active ? "Reconnect / Switch Account" : "Reconnect"}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() =>
                                  setConnectionToDisconnect({ id: connection.id, platformName })
                                }
                                className="text-red-600"
                                disabled={isLoading}
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Disconnect
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </Card>

        {/* Disconnect Confirmation Dialog */}
        <AlertDialog
          open={!!connectionToDisconnect}
          onOpenChange={(open) => !open && setConnectionToDisconnect(null)}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                Disconnect {connectionToDisconnect?.platformName}?
              </AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to disconnect this service? You will need to reconnect it
                later to use its features and AI agent tools again.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isDisconnecting}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={(e) => {
                  e.preventDefault();
                  handleDisconnect();
                }}
                disabled={isDisconnecting}
                className="bg-red-600 hover:bg-red-700"
              >
                {isDisconnecting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Disconnecting...
                  </>
                ) : (
                  "Disconnect"
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <UpgradePromptDialog
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          title="Upgrade to Starter"
          description="Connect third-party apps to power AI Workforce features and automate workflows. Connections are securely synced via Composio."
          feature="App Connections"
          planName="Starter"
          planFeatures={[
            "Unlimited Bids & Proposals",
            "Connect apps like Gmail, HubSpot & Slack",
            "Advanced Analytics",
          ]}
        />
      </div>
    </DashboardLayout>
  );
}
